/**
 * ResponseTimeCard Component
 * 
 * Displays agent response time KPIs and leaderboard.
 * 
 * Color coding:
 * - Under 30 min = Green (excellent)
 * - 30 to 60 min = Orange (acceptable)  
 * - Over 60 min = Red (unacceptable)
 * - Not yet responded = Grey with blinking indicator
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import HelpInfo from '../components/HelpInfo';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Progress } from '../components/ui/progress';
import { 
  Dialog,
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogTrigger 
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Clock,
  TrendingUp,
  AlertTriangle,
  RefreshCw,
  Trophy,
  Timer,
  CheckCircle,
  XCircle
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Response time color mapping
const getResponseTimeColor = (minutes) => {
  if (minutes === null || minutes === undefined) return 'grey';
  if (minutes <= 30) return 'green';
  if (minutes <= 60) return 'orange';
  return 'red';
};

const getColorClasses = (color) => {
  switch (color) {
    case 'green': return { text: 'text-green-600', bg: 'bg-green-100', border: 'border-green-500' };
    case 'orange': return { text: 'text-orange-600', bg: 'bg-orange-100', border: 'border-orange-500' };
    case 'red': return { text: 'text-red-600', bg: 'bg-red-100', border: 'border-red-500' };
    default: return { text: 'text-gray-500', bg: 'bg-gray-100', border: 'border-gray-400' };
  }
};

// `departmentFilter` (optional): accepted so the HOD Dashboard's
// Sales/Marketing dropdown can pass it through. We don't currently use
// it inside — the backend KPIs are aggregate, not per-agent — but it's
// here so callers don't need a wrapper and we can plumb it through
// later without a UI change.
// eslint-disable-next-line no-unused-vars
const ResponseTimeCard = ({ compact = false, agentView = false, departmentFilter = 'all' }) => {
  const { token, user } = useAuth();
  const [kpis, setKpis] = useState(null);
  const [myStats, setMyStats] = useState(null);
  // Clickable filter tiles — null means "show all", 'replied' or
  // 'waiting' focuses the rate bar on that bucket.
  const [leadFilter, setLeadFilter] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [unrespondedLeads, setUnrespondedLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showUnresponded, setShowUnresponded] = useState(false);

  const fetchKPIs = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/response-time/dashboard-kpis?token=${token}`);
      const data = await response.json();
      
      if (data.success) {
        setKpis(data.kpis?.today);
      }
    } catch (error) {
      console.error('Error fetching KPIs:', error);
    }
  }, [token]);

  const fetchMyStats = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/response-time/my-stats?token=${token}`);
      const data = await response.json();
      
      if (data.success) {
        setMyStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching my stats:', error);
    }
  }, [token]);

  const fetchLeaderboard = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/response-time/leaderboard?token=${token}&limit=10`);
      const data = await response.json();
      
      if (data.success) {
        setLeaderboard(data.leaderboard || []);
      }
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    }
  }, [token]);

  const fetchUnrespondedLeads = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/response-time/unresponded?token=${token}&hours_threshold=24&limit=20`);
      const data = await response.json();
      
      if (data.success) {
        setUnrespondedLeads(data.leads || []);
      }
    } catch (error) {
      console.error('Error fetching unresponded leads:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchKPIs(),
        fetchMyStats(),
        fetchLeaderboard(),
        fetchUnrespondedLeads()
      ]);
      setLoading(false);
    };
    
    loadData();

    // Auto-refresh every 30 seconds so freshly captured first-responses
    // appear on the agent's KPI card without a manual refresh. The full
    // leaderboard + unresponded list refreshes on a slower 2-minute cadence
    // since they're heavier and change less often.
    const fastInterval = setInterval(() => {
      fetchKPIs();
      fetchMyStats();
    }, 30000);
    const slowInterval = setInterval(() => {
      fetchLeaderboard();
      fetchUnrespondedLeads();
    }, 120000);

    // Refresh whenever the tab regains focus — common case after the user
    // does something in another tab and comes back expecting fresh data.
    const onFocus = () => {
      fetchKPIs();
      fetchMyStats();
    };
    window.addEventListener('focus', onFocus);

    return () => {
      clearInterval(fastInterval);
      clearInterval(slowInterval);
      window.removeEventListener('focus', onFocus);
    };
  }, [fetchKPIs, fetchMyStats, fetchLeaderboard, fetchUnrespondedLeads]);

  // Compact KPI display for widgets - aligned with AgentStatusDashboard
  const CompactKPIDisplay = () => {
    const avgColor = getColorClasses(kpis?.avg_color || 'grey');
    
    return (
      <Card className="h-full" data-testid="response-time-card-compact">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center justify-between">
            <span className="flex items-center gap-2 flex-wrap">
              <Timer className="h-4 w-4" />
              Response Time
              <HelpInfo
                align="inline"
                title="How to read Response Time"
                steps={[
                  <>This tracks how fast new leads get a first reply <em>today</em>. The headline is the first-reply average across the team.</>,
                  <>The two tiles split today's new leads: <strong className="text-emerald-600">Leads replied to</strong> (someone responded) and <strong className="text-amber-600">Still waiting</strong> (no reply yet).</>,
                  <>The <em>Awaiting Response</em> badge flags when leads are still sitting unanswered — clear it by replying to them.</>,
                  'When no new leads were assigned today, the panel says so — there is simply nothing waiting on a reply.',
                ]}
                tip="First-reply speed is the single biggest lever on lead conversion — a lead answered within 5 minutes is far more likely to convert than one answered an hour later."
              />
            </span>
            <Button size="icon" variant="ghost" onClick={() => { fetchKPIs(); fetchMyStats(); }}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Avg Response — how quickly the team first replies to new
              leads. The badge shows the average time (e.g. "12m" / "1h")
              or "No replies yet" when no one has replied yet today. */}
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-muted-foreground">First-reply average (today)</span>
            <Badge className={`${avgColor.bg} ${avgColor.text} px-3`}>
              {kpis?.avg_display || 'No replies yet'}
            </Badge>
          </div>

          {/* Clickable filter tiles — toggle the focus of the section
              below between Replied / Waiting / All. */}
          <div className="grid grid-cols-2 gap-2 mb-3 text-center">
            {[
              { k: 'replied', label: 'Leads replied to', value: kpis?.leads_responded || 0,
                bg: 'bg-green-50',  border: 'border-green-200',  active: 'ring-green-500 border-green-500',  text: 'text-green-700',  Icon: CheckCircle },
              { k: 'waiting', label: 'Still waiting',    value: kpis?.leads_unresponded || 0,
                bg: 'bg-orange-50', border: 'border-orange-200', active: 'ring-orange-500 border-orange-500', text: 'text-orange-700', Icon: XCircle },
            ].map((tile) => (
              <button
                key={tile.k}
                type="button"
                onClick={() => setLeadFilter(leadFilter === tile.k ? null : tile.k)}
                className={`rounded-md border py-2 text-center transition ${tile.bg} ${
                  leadFilter === tile.k ? `ring-2 ${tile.active}` : tile.border + ' hover:opacity-90'
                }`}
              >
                <div className={`flex items-center justify-center gap-1.5 ${tile.text}`}>
                  <tile.Icon className="w-3.5 h-3.5" />
                  <span className="text-base font-semibold">{tile.value}</span>
                </div>
                <div className={`text-[11px] ${tile.text}`}>{tile.label}</div>
              </button>
            ))}
          </div>

          {/* Filter banner */}
          {leadFilter && (
            <div className="flex items-center justify-between text-xs mb-2 px-2 py-1 rounded bg-slate-50 border border-slate-200">
              <span>
                Showing: <strong>{leadFilter === 'replied' ? 'Leads replied to' : 'Leads still waiting'}</strong>
              </span>
              <button onClick={() => setLeadFilter(null)} className="text-slate-500 hover:text-slate-700 underline">
                Clear
              </button>
            </div>
          )}

          {/* Response rate progress bar — adapts to the active filter so
              the % reads as "responded ÷ total" or "pending ÷ total". */}
          {kpis?.leads_assigned > 0 ? (
            <div className="space-y-1.5">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>
                  {leadFilter === 'replied' ? 'Replied share of today'
                   : leadFilter === 'waiting' ? 'Waiting share of today'
                   : 'Response rate today'}
                </span>
                <span>
                  {leadFilter === 'waiting'
                    ? `${Math.max(0, 100 - (kpis?.response_rate || 0))}% of ${kpis?.leads_assigned} leads`
                    : `${kpis?.response_rate || 0}% of ${kpis?.leads_assigned} leads`}
                </span>
              </div>
              <Progress
                value={leadFilter === 'waiting' ? Math.max(0, 100 - (kpis?.response_rate || 0)) : (kpis?.response_rate || 0)}
                className="h-2"
              />
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-2">
              No new leads assigned today — nothing waiting on a reply.
            </p>
          )}
        </CardContent>
      </Card>
    );
  };

  // Agent-specific stats view
  const AgentStatsCard = () => {
    if (!myStats) return null;
    
    const avgColor = getColorClasses(myStats.avg_color);
    
    return (
      <Card data-testid="my-response-stats">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="h-4 w-4" />
            My Response Time Stats
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Average Response</p>
              <p className={`text-2xl font-bold ${avgColor.text}`}>
                {myStats.avg_display}
              </p>
            </div>
            
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Response Rate</p>
              <p className="text-2xl font-bold">
                {myStats.response_rate}%
              </p>
            </div>
            
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Total Assigned</p>
              <p className="text-lg font-semibold">{myStats.total_assigned}</p>
            </div>
            
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Awaiting Response</p>
              <p className="text-lg font-semibold text-orange-600">
                {myStats.awaiting_response}
              </p>
            </div>
          </div>
          
          {myStats.min_response_time && (
            <div className="mt-4 pt-4 border-t flex justify-between text-sm text-muted-foreground">
              <span>Fastest: {myStats.min_response_time} min</span>
              <span>Slowest: {myStats.max_response_time} min</span>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  // Leaderboard dialog
  const LeaderboardDialog = () => (
    <Dialog open={showLeaderboard} onOpenChange={setShowLeaderboard}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" onClick={fetchLeaderboard}>
          <Trophy className="h-4 w-4 mr-2" />
          Leaderboard
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-yellow-500" />
            Response Time Leaderboard
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[400px] overflow-y-auto">
          {leaderboard.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">No data available</p>
          ) : (
            leaderboard.map((entry, idx) => {
              const colorClasses = getColorClasses(entry.avg_color);
              return (
                <div 
                  key={entry.agent_id}
                  className={`flex items-center justify-between p-3 rounded-lg ${idx < 3 ? 'bg-muted/50' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold w-8">
                      {entry.badge || `#${entry.rank}`}
                    </span>
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={entry.agent_picture} />
                      <AvatarFallback>{entry.agent_name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium text-sm">{entry.agent_name}</p>
                      <p className="text-xs text-muted-foreground">{entry.total_leads} leads</p>
                    </div>
                  </div>
                  <Badge className={`${colorClasses.bg} ${colorClasses.text}`}>
                    {entry.avg_display}
                  </Badge>
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );

  // Unresponded leads dialog
  const UnrespondedLeadsDialog = () => (
    <Dialog open={showUnresponded} onOpenChange={setShowUnresponded}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="text-orange-600" onClick={fetchUnrespondedLeads}>
          <AlertTriangle className="h-4 w-4 mr-2" />
          Unresponded ({unrespondedLeads.length})
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            Leads Awaiting Response (24h+)
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[400px] overflow-y-auto">
          {unrespondedLeads.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">
              <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
              All leads have been responded to!
            </p>
          ) : (
            unrespondedLeads.map(lead => {
              const colorClasses = getColorClasses(lead.wait_color);
              return (
                <div 
                  key={lead.id}
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 cursor-pointer"
                  onClick={() => window.location.href = `/contacts/${lead.id}`}
                >
                  <div>
                    <p className="font-medium">{lead.name || 'Unknown Lead'}</p>
                    <p className="text-sm text-muted-foreground">
                      {lead.email || lead.phone || 'No contact info'}
                    </p>
                  </div>
                  <div className="text-right">
                    <Badge className={`${colorClasses.bg} ${colorClasses.text}`}>
                      {lead.wait_display}
                    </Badge>
                    <p className="text-xs text-muted-foreground mt-1">
                      {lead.source || 'Unknown source'}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">Loading response times...</span>
        </CardContent>
      </Card>
    );
  }

  if (compact) {
    return <CompactKPIDisplay />;
  }

  if (agentView) {
    return <AgentStatsCard />;
  }

  // Full dashboard view for CEO/HOD
  const avgColor = getColorClasses(kpis?.avg_color || 'grey');

  return (
    <div className="space-y-6" data-testid="response-time-dashboard">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Leads Today</p>
              <p className="text-2xl font-bold">{kpis?.leads_assigned || 0}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Responded</p>
              <p className="text-2xl font-bold text-green-600">{kpis?.leads_responded || 0}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Avg Response Time</p>
              <p className={`text-2xl font-bold ${avgColor.text}`}>
                {kpis?.avg_display || 'No data'}
              </p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Response Rate</p>
              <p className="text-2xl font-bold">{kpis?.response_rate || 0}%</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Action buttons */}
      <div className="flex gap-2 flex-wrap">
        <Button variant="outline" size="sm" onClick={() => { fetchKPIs(); fetchLeaderboard(); fetchUnrespondedLeads(); }}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
        <LeaderboardDialog />
        <UnrespondedLeadsDialog />
      </div>

      {/* Leaderboard preview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Top Performers (Response Time)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {leaderboard.slice(0, 5).map((entry, idx) => {
              const colorClasses = getColorClasses(entry.avg_color);
              return (
                <div 
                  key={entry.agent_id}
                  className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold w-6">
                      {entry.badge || `#${entry.rank}`}
                    </span>
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={entry.agent_picture} />
                      <AvatarFallback>{entry.agent_name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <span className="font-medium">{entry.agent_name}</span>
                  </div>
                  <Badge className={`${colorClasses.bg} ${colorClasses.text}`}>
                    {entry.avg_display}
                  </Badge>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResponseTimeCard;
